import './App.css';
import React, {useEffect} from "react";
import {BrowserRouter as Router, Redirect, Route, Switch} from "react-router-dom";
import Layout from "./components/Layout/Layout";
import RequestService, {rolesState} from "./services/requestService";
import Profile from "./scenes/Profile/Profile";
import Help from "./scenes/Help/Help";
import {SnackbarProvider} from 'notistack';
import Page from "./scenes/Page/Page";
import {createTheme, CssBaseline, ThemeProvider} from "@mui/material";
import UserRoute from "./components/route/UserRoute";
import LoginPortal from "./scenes/LoginPortal/LoginPortal";
import Error from "./scenes/Error/Error";
import {getDesignTokens} from "./themeParts";
import {atom, useRecoilState, useRecoilValue} from "recoil";
import {localStorageEffect} from "./services/localStorage";
import * as locales from '@mui/material/locale';
import {useTranslation} from "react-i18next";
import History from "./scenes/History/History";
import Home from "./scenes/Home/Home";
import Title from "./scenes/Title/Title";
import ScrollToTop from "./components/route/ScrollToTop";
import Collection from "./scenes/Collection/Collection";
import * as PropTypes from "prop-types";
import {USER_ROLES} from "./constants";
import Admin from "./scenes/admin/Admin";

export const themeState = atom({
    key: 'themeState', // unique ID (with respect to other atoms/selectors)
    default: 'light', // default value
    effects_UNSTABLE: [
        localStorageEffect('theme'),
    ]
});

export const ColorModeContext = React.createContext({
    toggleColorMode: () => {
    }
});

function CheckRole(props) {
    const roles = useRecoilValue(rolesState);

    return (
        props.roles.reduce((p, c) => p & roles.includes(c), true) ?
            props.children :
            <Error type="notfound"/>
    );
}

CheckRole.propTypes = {
    roles: PropTypes.arrayOf(PropTypes.oneOf(Object.values(USER_ROLES))).isRequired,
    children: PropTypes.elementType,
}

CheckRole.propTypes = {children: PropTypes.node};

function App() {
    const {i18n} = useTranslation('common');
    const [locale, setLocale] = React.useState("frFR");
    const [mode, setMode] = useRecoilState(themeState);
    const colorMode = React.useMemo(
        () => ({
            // The dark mode switch would invoke this method
            toggleColorMode: () => {
                setMode((prevMode) =>
                    prevMode === 'light' ? 'dark' : 'light',
                );
            },
        }), [setMode]);

    useEffect(() => {
        switch (i18n.language) {
            case "en":
                setLocale("enEN");
                break;
            default:
                setLocale("frFR");
        }
    }, [i18n.language])

    // Update the theme only if the mode changes
    const theme = React.useMemo(() => createTheme(getDesignTokens(mode), locales[locale]), [mode, locale]);

    return (
        <ColorModeContext.Provider value={colorMode}>
            <ThemeProvider theme={theme}>
                <CssBaseline/>
                <SnackbarProvider maxSnack={6}>
                    <Router>
                        <RequestService>
                            <ScrollToTop>
                                <Switch>
                                    <Route exact path={"/portal"}><LoginPortal/></Route>
                                    <Route>
                                        <Layout>
                                            <Switch>
                                                <UserRoute exact path="/profile"><Redirect
                                                    to="/profile/info"/></UserRoute>
                                                <UserRoute path="/profile/:tab"><Profile/></UserRoute>
                                                <Route exact path="/help"><Help/></Route>
                                                <Route exact path="/history"><History/></Route>
                                                <Route exact path="/title"><Redirect to="/"/></Route>
                                                <Route exact path="/title/:id"><Title/></Route>
                                                <Route exact path="/page"><Redirect to="/"/></Route>
                                                <Route exact path="/page/:id"><Page/></Route>
                                                <Route exact path="/collection/:id"><Collection/></Route>
                                                <Route exact path="/error/:type?"><Error/></Route>
                                                <Route exact path="/admin">
                                                    <Redirect to="/admin/users"/>
                                                </Route>
                                                <Route exact path="/admin/*">
                                                    <CheckRole roles={[USER_ROLES.ADMIN]}><Admin/></CheckRole>
                                                </Route>
                                                <Route path="/pivan/api"><Redirect to="/error/noresponse"/></Route>
                                                <Route exact path="/"><Home/></Route>
                                                <Route path="*"><Redirect to="/"/></Route>
                                            </Switch>
                                        </Layout>
                                    </Route>
                                </Switch>
                            </ScrollToTop>
                        </RequestService>
                    </Router>
                </SnackbarProvider>
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
}

export default App;
